/**
 * Which failure message a rejected password login should show.
 *
 * `Api::AuthenticationController#create` already distinguishes the two cases
 * and always has:
 *
 *   { error: 'Authentication failed, SSO User' }  401  -- User.sso_authed matched this email
 *   { error: 'Authentication failed' }            401  -- wrong password, or no such account
 *
 * The SPA threw that away and printed a single hardcoded line, "Sorry, the
 * email or password you entered is incorrect", for both. For an account whose
 * `sso` is 'google' that sentence is actively wrong: there is no password to
 * get right, and the "Forgot Password?" link it points at cannot help. People
 * were being sent to reset a password that could never log them in.
 *
 * Matching is deliberately loose and fails SAFE. The server string is not
 * localised and is not a stable contract, so anything unrecognised -- a
 * different message, an Error, undefined, a network failure -- falls through to
 * the generic password message, which is what shipped before this change.
 */
const SSO_MARKER = 'sso user'

const errorText = (error: unknown): string => {
  if (typeof error === 'string') return error
  if (error && typeof (error as { message?: unknown }).message === 'string') return (error as { message: string }).message
  if (error && typeof (error as { error?: unknown }).error === 'string') return (error as { error: string }).error
  return ''
}

export const isSsoAccountError = (error: unknown): boolean => errorText(error).toLowerCase().includes(SSO_MARKER)

export type LoginErrorKind = 'sso' | 'credentials'

export const loginErrorKind = (error: unknown): LoginErrorKind => (isSsoAccountError(error) ? 'sso' : 'credentials')
